import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle, ArrowRight } from 'lucide-react';

const BenefitsSection = () => {
  const realBenefits = [
    {
      title: "Direkter Kundenkontakt",
      description: "Push-Notifications, die ankommen und gelesen werden - ohne Spam-Filter oder E-Mail-Chaos.",
      example: "Neue Angebote, Terminerinnerungen oder wichtige Updates erreichen deine Kunden sofort."
    },
    {
      title: "Immer verfügbar",
      description: "Deine Kunden können 24/7 bestellen, buchen oder Informationen abrufen - auch wenn du schläfst.",
      example: "Online-Shop, Terminbuchung oder Kundenservice - alles in einer App."
    },
    {
      title: "Einfachere Prozesse",
      description: "Was früher kompliziert war, wird mit einer App zum Kinderspiel - für dich und deine Kunden.",
      example: "QR-Code scannen statt Formulare ausfüllen, One-Click-Bestellungen statt langwierige Checkout-Prozesse."
    },
    {
      title: "Bessere Daten",
      description: "Du verstehst deine Kunden besser, weil du siehst, wie sie deine App nutzen.",
      example: "Welche Features werden genutzt? Wo steigen Nutzer aus? Was funktioniert gut?"
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="container">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Was bringt dir eine <span className="text-blue-600">eigene App</span> wirklich?
          </h2>
          <p className="text-lg text-gray-600">
            Keine übertriebenen Versprechen - hier sind die echten, messbaren Vorteile, 
            die unsere Kunden erlebt haben.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-16">
          {realBenefits.map((benefit, index) => (
            <Card key={index} className="border-0 shadow-lg bg-white h-full hover:shadow-xl transition-all duration-300">
              <CardContent className="p-8 h-full flex flex-col">
                <div className="flex items-start mb-4">
                  <CheckCircle className="h-6 w-6 text-green-500 mr-3 mt-1 flex-shrink-0" />
                  <h3 className="text-xl font-bold">{benefit.title}</h3>
                </div>
                
                <p className="text-gray-700 mb-4 flex-grow">
                  {benefit.description}
                </p>
                
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-blue-800 text-sm">
                    <strong>Beispiel:</strong> {benefit.example}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Realistic Expectations */}
        <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl shadow-xl p-8 max-w-4xl mx-auto text-center">
          <h3 className="text-2xl font-bold mb-4 text-gray-900">
            Ehrlich gesagt...
          </h3>
          <p className="text-lg text-gray-600 mb-8">
            Eine App ist kein Wundermittel. Sie funktioniert nur, wenn sie gut gemacht ist 
            und einen echten Nutzen für deine Kunden bietet. Genau darauf konzentrieren wir uns.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h4 className="font-bold text-gray-900 mb-2">📱 Nutzerfreundlich</h4>
              <p className="text-gray-700 text-sm">Einfach zu bedienen, auch für weniger technikaffine Nutzer</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h4 className="font-bold text-gray-900 mb-2">🔧 Zuverlässig</h4>
              <p className="text-gray-700 text-sm">Funktioniert stabil und wird regelmäßig gewartet</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h4 className="font-bold text-gray-900 mb-2">💡 Sinnvoll</h4>
              <p className="text-gray-700 text-sm">Löst echte Probleme und bietet echten Mehrwert</p>
            </div>
          </div>
          
          <a 
            href="#contact" 
            className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
          >
            Lass uns ehrlich über deine App-Idee sprechen
            <ArrowRight className="ml-2 h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection; 